import React, { useEffect, useState } from "react";
import { Modal, Button, Row, Col, Container, Form } from "react-bootstrap";
import { Formik, Field } from "formik";
import * as Yup from "yup";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import {
	getAnIssueAsync,
	updateIssueAsync,
	setIssueId,
} from "../../../../slices/project-management/project-management-slice";
import { FormControl, MenuItem, Select } from "@mui/material";
import { toast } from "react-toastify";
import { RootState } from "../../../../store/store";

interface UpdateIssueValues {
	issue_name: string;
	associated_task: number;
	priority: string;
	assigned_to: number;
	status: string;
	image: string;
	image_url: string;
}

interface UpdateIssueProps {
	closeModal: () => void;
	show: boolean;
	project_id: string | undefined | null;
	id: string;
	onEditSuccess: () => void;
}

const UpdateIssue: React.FC<UpdateIssueProps> = ({
	closeModal,
	show,
	project_id,
	id,
	onEditSuccess,
}) => {
	const dispatch = useAppDispatch();
	const company_id = localStorage.getItem("userCompanyID");
	const issue = useAppSelector((state) => state.projectManagement.issuesData);

	console.log("Selected issue from Redux:", issue);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (project_id && company_id && id !== "") {
			dispatch(setIssueId(id));
			console.log("Fetching issue with ID:", id);
			dispatch(getAnIssueAsync({ company_id, id: id, projectId: project_id })).then(() =>
				setLoading(false),
			);
		} else {
			console.log("Issue is not set");
		}
	}, [dispatch, company_id, id, project_id]);
	const tasks = useAppSelector((state) => state.projectManagement.tasks?.data);
	const users = useAppSelector((state) => state.user.users?.data);
	const companyId = useAppSelector(
		(state: RootState) => state.auth.user?.company_id || "",
	);

	if (loading || !issue) {
		return null;
	}

	return (
		<Container fluid className="">
			<Modal
				centered
				size="lg"
				show={show}
				onHide={closeModal}
				style={{ maxHeight: "100vh", overflowY: "auto" }}
			>
				<div className="px-5">
					<Formik
						enableReinitialize
						initialValues={{
							issue_name: issue?.issue_name || "",
							associated_task: issue?.associated_task_id || 0,
							assigned_to: issue?.assigned_to_id || 0,
							priority: issue?.priority || "",
							status: issue?.status || "",
							image: issue?.image || "",
							image_url: issue?.image_url || "",
						}}
						validationSchema={Yup.object().shape({
							issue_name: Yup.string().required("Required"),
							associated_task: Yup.number().required("Required"),
							assigned_to: Yup.string().required("Required"),
							priority: Yup.string().required("Required"),
							status: Yup.string().required("Required"),
							image: Yup.mixed().nullable()
						})}
						onSubmit={(values: UpdateIssueValues, { setSubmitting }) => {
							console.log("values", values)
							if (companyId) {
								const updateIssuesData = {
									issue_name: values.issue_name,
									associated_task: values.associated_task,
									assigned_to: values.assigned_to,
									priority: values.priority,
									status: values.status,
									company_id: companyId,
									projectId: project_id || "",
									image: values.image || "",
									id: id,
									image_url: values.image_url,
								};
								dispatch(updateIssueAsync(updateIssuesData))
									.unwrap()
									.then(() => {
										onEditSuccess();
										closeModal();
									})
									.catch((error) => {
										const errorMessage = error.message
											? error.message
											: "Something went wrong!";
										toast.error(errorMessage);
									})
									.finally(() => {
										setSubmitting(false);
									});
							} else {
								console.error("project_id or company_id is undefined");
							}
						}}

						validateOnChange
						validateOnBlur
					>
						{({
							errors,
							touched,
							handleSubmit,
							isSubmitting,
							values,
							handleChange,
							dirty,
							setFieldValue,
						}) => (
							<Form onSubmit={handleSubmit}>
								<Modal.Header closeButton className="text-start mt-2">
									<h4>Edit issue</h4>
								</Modal.Header>
								<Modal.Body>
									<Row>
										<Col md={12} className="mt-2 w-50">
											<label className="my-2">
												Issue name <span className="text-danger">*</span>
											</label>
											<Field
												type="text"
												name="issue_name"
												className={`fluent-input  ${touched.issue_name && errors.issue_name
													? "is-invalid"
													: ""
													}`}
											></Field>
										</Col>
										<Col md={12} className="mt-2 w-50">
											<label className="my-2">
												Associated task <span className="text-danger">*</span>
											</label>
											<FormControl fullWidth>
												{/* <InputLabel>Task</InputLabel> */}
												<Select
													// label="Member"
													name="associated_task"
													value={values.associated_task || ""}
													className={`fluent-input  ${touched.associated_task && errors.associated_task
														? "is-invalid"
														: ""
														}`}
													onChange={handleChange}
													required
												>
													{tasks !== undefined && tasks !== null ? (
														tasks?.map((task: any) => (
															<MenuItem key={task.id} value={task.id}>
																{task.task_name}
															</MenuItem>
														))
													) : (
														<MenuItem value="">John Doe</MenuItem>
													)}
												</Select>
											</FormControl>
										</Col>
									</Row>
									<Row>
										<Col md={12} className="mt-2 w-50">
											<label className="my-2">
												Assign task to <span className="text-danger">*</span>
											</label>
											<FormControl fullWidth>
												{/* <InputLabel>Member</InputLabel> */}
												<Select
													// label="Member"
													name="assigned_to"
													value={values.assigned_to || ""}
													className={`fluent-input  ${touched.assigned_to && errors.assigned_to
														? "is-invalid"
														: ""
														}`}
													onChange={handleChange}
													required
												>
													{users !== undefined && users !== null ? (
														users?.map((user) => (
															<MenuItem key={user.id} value={user.id}>
																{user.first_name} {user.last_name}
															</MenuItem>
														))
													) : (
														<MenuItem value="">John Doe</MenuItem>
													)}
												</Select>
											</FormControl>
											{touched.assigned_to && errors.assigned_to ? (
												<p className="error-message mb-0 d-inline-flex align-items-center">
													{errors.assigned_to}
												</p>
											) : (
												<p className="error-message-placeholder mb-0">
													no errors
												</p>
											)}
										</Col>
										<Col md={12} className="mt-2 w-50">
											<label className="my-2">
												Priority <span className="text-danger">*</span>
											</label>
											<FormControl fullWidth>
												{/* <InputLabel>Priority</InputLabel> */}
												<Select
													// label="Member"
													name="priority"
													className={`fluent-input  ${touched.priority && errors.priority
														? "is-invalid"
														: ""
														}`}
													value={values.priority}
													onChange={handleChange}
													required
												>
													<MenuItem value="High">High</MenuItem>
													<MenuItem value="Medium">Medium</MenuItem>
													<MenuItem value="Low">Low</MenuItem>
												</Select>
											</FormControl>
										</Col>
									</Row>
									<Row>
										<Col md={12} className="mt-2 w-50">
											<label className="">
												<p>
													Status <span className="text-danger">*</span>
												</p>
											</label>
											<FormControl fullWidth>
												{/* <InputLabel>Status</InputLabel> */}
												<Select
													// label="status"
													name="status"
													className={`fluent-input  ${touched.status && errors.status
														? "is-invalid"
														: ""
														}`}
													value={values.status}
													onChange={handleChange}
													required
												>
													<MenuItem value="To do">To do</MenuItem>
													<MenuItem value="In progress">In progress</MenuItem>
													<MenuItem value="Done">Done</MenuItem>
												</Select>
											</FormControl>
										</Col>

										<Col md={12} className="mt-2 w-50">
											<label>
												<p>
													Image <span className="text-danger">*</span>
												</p>
											</label>

											<input
												type="file"
												name="image"
												className={`fluent-input ${touched.image && errors.image ? "is-invalid" : ""}`}
												accept="image/*"
												onChange={(event) => {
													const file = event.currentTarget.files?.[0];
													setFieldValue("image", file ?? "");
												}}
											/>
											{touched.image && errors.image && <p className="text-danger">{errors.image}</p>}

											{values.image_url && typeof values.image === "string" && (
												<div className="float-end">
													<div>
														<small>
															{values.image_url.split("/").pop()}
														</small>
													</div>
												</div>
											)}
										</Col>

									</Row>

									<Modal.Footer className="mt-5">
										<Row>
											<Col md={6}>
												<div className=" mt-1">
													<Button onClick={closeModal} className="cancel-btn">
														<span className="text-center">Cancel </span>{" "}
													</Button>
												</div>
											</Col>
											<Col md={6}>
												<div className=" mt-1">
													<Button
														className="border-0 create-btn bg-color-1"
														onClick={() => handleSubmit()}
														disabled={!dirty || isSubmitting}
													>
														{`${isSubmitting ? "Editing issue..." : "Edit issue"
															} `}
													</Button>
												</div>
											</Col>
										</Row>
									</Modal.Footer>
								</Modal.Body>
							</Form>
						)}
					</Formik>
				</div>
			</Modal>
		</Container>
	);
};

export default UpdateIssue;
